import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentType, ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Position,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import Select from '@sorare/core/src/atoms/inputs/Select';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Title6 } from '@sorare/core/src/atoms/typography';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { HeaderMetrics } from '@sorare/core/src/components/seo/HeaderMetrics';
import { cardAttributes, fantasy } from '@sorare/core/src/lib/glossary';
import {
  getAppearancePercentage,
  positionNames,
} from '@sorare/core/src/lib/players';

import { FixtureChart } from './FixtureChart';
import {
  LastScores_anyPlayer,
  LastScores_playerGameScoreInterface,
} from './__generated__/index.graphql';

type Props = {
  so5Scores: (LastScores_playerGameScoreInterface | null)[];
  player: LastScores_anyPlayer;
  cardPositions?: Position[];
  setPosition?: (position?: Position) => void;
  selectedPosition?: Position;
  InfiniteScrollLoader?: ReactNode;
  TitleComponent?: ComponentType<{ children: ReactNode }>;
  onScoreSelect?: (scoreId: string) => void;
};

const Root = styled(Vertical).attrs({ gap: 4 })`
  overflow: auto;
`;
const Section = styled(Vertical)`
  overflow: hidden;
`;
const Title = styled(SBHorizontal)`
  flex-wrap: wrap;
`;

const PerformancesSummary = ({ player }: { player: LastScores_anyPlayer }) => {
  return (
    <HeaderMetrics
      fullWidth
      metrics={[
        ...(player.sport === Sport.FOOTBALL
          ? [
              {
                label: <FormattedMessage {...cardAttributes.last5} />,
                body: (
                  <PlayerScore
                    player={player}
                    mode={AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE}
                  />
                ),
                subLabel: (
                  <FormattedMessage
                    {...fantasy.percentagePlayed}
                    values={{
                      percent:
                        +getAppearancePercentage(
                          AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
                          player
                        ) / 100,
                    }}
                  />
                ),
              },
            ]
          : []),
        {
          label: <FormattedMessage {...cardAttributes.last15} />,
          body: (
            <PlayerScore
              player={player}
              mode={AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE}
            />
          ),
          subLabel: (
            <FormattedMessage
              {...fantasy.percentagePlayed}
              values={{
                percent:
                  +getAppearancePercentage(
                    AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
                    player
                  ) / 100,
              }}
            />
          ),
        },
      ]}
    />
  );
};

const LastScores = ({
  cardPositions = [],
  setPosition,
  selectedPosition,
  so5Scores,
  player,
  InfiniteScrollLoader,
  TitleComponent = Title6,
  onScoreSelect,
}: Props) => {
  const { formatMessage } = useIntl();

  if (!so5Scores?.length) return null;

  return (
    <Root>
      <Section>
        <SBHorizontal>
          <TitleComponent>
            <FormattedMessage id="LastScores.stats" defaultMessage="Stats" />
          </TitleComponent>
          {cardPositions.length > 1 && setPosition && (
            <Title>
              <Select
                menuLateralAlignment="right"
                value={
                  selectedPosition && {
                    value: selectedPosition,
                    label: formatMessage(positionNames[selectedPosition]),
                  }
                }
                onChange={e => {
                  setPosition(e!.value as Position);
                }}
                options={cardPositions.map(value => ({
                  value,
                  label: formatMessage(positionNames[value]),
                }))}
              />
            </Title>
          )}
        </SBHorizontal>

        <PerformancesSummary player={player} />
      </Section>
      <Section>
        <Title6 as="h2">
          <FormattedMessage
            id="LastScores.performance"
            defaultMessage="Performance"
          />
        </Title6>
        <FixtureChart
          so5Scores={so5Scores}
          player={player}
          InfiniteScrollLoader={InfiniteScrollLoader}
          onScoreSelect={onScoreSelect}
        />
      </Section>
    </Root>
  );
};

LastScores.fragments = {
  playerGameScoreInterface: gql`
    fragment LastScores_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      ...FixtureChart_playerGameScoreInterface
    }
    ${FixtureChart.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<LastScores_playerGameScoreInterface>,
  anyPlayer: gql`
    fragment LastScores_anyPlayer on AnyPlayerInterface {
      slug
      sport
      lastFiveSo5Appearances
      lastFifteenSo5Appearances
      ...FixtureChart_anyPlayer
      ...PlayerScore_anyPlayer
      ...getAppearancePercentage_anyPlayerInterface
    }
    ${FixtureChart.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
    ${getAppearancePercentage.fragments.anyPlayer}
  ` as TypedDocumentNode<LastScores_anyPlayer>,
};

export default LastScores;
